// ============================================================================
// Permission Management Utilities
// ============================================================================

import { UserRole } from '../types';

export interface Permission {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canApprove: boolean;
  canExport: boolean;
  canManageUsers: boolean;
}

/**
 * Get permissions based on user role
 */
export const getPermissions = (role: UserRole): Permission => {
  switch (role) {
    case 'Admin':
      return {
        canView: true,
        canCreate: true,
        canEdit: true,
        canDelete: true,
        canApprove: true,
        canExport: true,
        canManageUsers: true,
      };

    case 'Manager':
      return {
        canView: true,
        canCreate: true,
        canEdit: true,
        canDelete: false,
        canApprove: true,
        canExport: true,
        canManageUsers: false,
      };

    case 'Contributor':
      return {
        canView: true,
        canCreate: true,
        canEdit: false, // Can only edit own drafts
        canDelete: false,
        canApprove: false,
        canExport: false,
        canManageUsers: false,
      };

    case 'Analyst':
      return {
        canView: true,
        canCreate: true,
        canEdit: true,
        canDelete: false,
        canApprove: false,
        canExport: true,
        canManageUsers: false,
      };

    case 'Viewer':
      return {
        canView: true,
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canApprove: false,
        canExport: true, // Viewers can export approved data
        canManageUsers: false,
      };

    default:
      return {
        canView: false,
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canApprove: false,
        canExport: false,
        canManageUsers: false,
      };
  }
};

/**
 * Check if user can perform specific action
 */
export const canPerformAction = (
  role: UserRole,
  action: keyof Permission
): boolean => {
  const permissions = getPermissions(role);
  return permissions[action];
};

/**
 * Check if user can edit specific data entry
 */
export const canEditEntry = (
  userRole: UserRole,
  userId: string,
  entryStatus: string,
  entryOwnerId?: string
): boolean => {
  // Admin can edit all entries including approved ones
  if (userRole === 'Admin') {
    return true;
  }

  // Contributors can only edit their own draft entries
  if (userRole === 'Contributor') {
    return (
      entryStatus === 'draft' &&
      entryOwnerId === userId
    );
  }

  return false;
};

/**
 * Check if user can delete specific data entry
 */
export const canDeleteEntry = (
  userRole: UserRole,
  userId: string,
  entryStatus: string,
  entryOwnerId?: string
): boolean => {
  // Admin can delete any entry, including approved ones
  if (userRole === 'Admin') {
    return true;
  }

  return false;
};

/**
 * Filter data based on user role
 */
export const filterDataByRole = <T extends { status: string; uploadedBy?: string }>(
  data: T[],
  userRole: UserRole,
  userId: string
): T[] => {
  switch (userRole) {
    case 'Admin':
      // Admins see all data
      return data;

    case 'Contributor':
      // Contributors see only their own submissions and approved data
      return data.filter(
        item => item.uploadedBy === userId || item.status === 'approved'
      );

    case 'Viewer':
      // Viewers see only approved data
      return data.filter(item => item.status === 'approved');

    default:
      return [];
  }
};

/**
 * Get allowed actions for a data entry
 */
export const getAllowedActions = (
  userRole: UserRole,
  userId: string,
  entryStatus: string,
  entryOwnerId?: string
) => {
  return {
    canEdit: canEditEntry(userRole, userId, entryStatus, entryOwnerId),
    canDelete: canDeleteEntry(userRole, userId, entryStatus, entryOwnerId),
    canApprove: (userRole === 'Admin' || userRole === 'Manager') && entryStatus === 'submitted',
    canReject: (userRole === 'Admin' || userRole === 'Manager') && entryStatus === 'submitted',
  };
};
